import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FormField } from "@/components/common/FormField";
import { cnInput } from "@/components/common/formClasses";
import { usePermissionList, useUpsertPermission } from "../hooks/usePermissions";
import { usePermissionStore } from "../stores/permissionStore";

const schema = z.object({
  code: z.string().min(1, "code 는 필수입니다"),
  label: z.string().min(1, "label 은 필수입니다"),
  permissionGroup: z.string().min(1, "permissionGroup 은 필수입니다"),
  description: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

export function PermissionDialog() {
  const open = usePermissionStore((s) => s.dialogOpen);
  const editingCode = usePermissionStore((s) => s.editingCode);
  const close = usePermissionStore((s) => s.closeDialog);

  const { data: permissions } = usePermissionList();
  const upsert = useUpsertPermission();

  const isEdit = !!editingCode;
  const editing = isEdit
    ? permissions?.find((p) => p.code === editingCode)
    : undefined;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      code: "",
      label: "",
      permissionGroup: "",
      description: "",
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        code: editing?.code ?? "",
        label: editing?.label ?? "",
        permissionGroup: editing?.permissionGroup ?? "",
        description: editing?.description ?? "",
      });
    }
  }, [open, editing, reset]);

  const onSubmit = (values: FormValues) => {
    upsert.mutate({
      code: values.code.trim(),
      label: values.label.trim(),
      permissionGroup: values.permissionGroup.trim(),
      description: values.description?.trim() || undefined,
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) close();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-sm">
            {isEdit ? "권한 수정" : "권한 추가"}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {isEdit
              ? "code 는 변경할 수 없습니다."
              : "신규 권한을 등록합니다. code 는 영구 식별자로 사용됩니다."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
          <FormField label="code" required error={errors.code?.message}>
            <input
              {...register("code")}
              disabled={isEdit}
              placeholder="order-read"
              className={cnInput("font-mono disabled:bg-gray-100 disabled:text-gray-500")}
            />
          </FormField>

          <FormField label="label" required error={errors.label?.message}>
            <input
              {...register("label")}
              placeholder="주문 조회"
              className={cnInput()}
            />
          </FormField>

          <FormField
            label="permissionGroup"
            required
            error={errors.permissionGroup?.message}
          >
            <input
              {...register("permissionGroup")}
              placeholder="order"
              className={cnInput("font-mono")}
            />
          </FormField>

          <FormField label="description">
            <textarea
              {...register("description")}
              placeholder="권한 설명"
              rows={2}
              className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-xs outline-none focus:border-blue-500"
            />
          </FormField>

          <div className="flex justify-end gap-2 pt-1">
            <Button
              type="button"
              variant="outline"
              onClick={close}
              className="text-xs"
            >
              취소
            </Button>
            <Button
              type="submit"
              disabled={upsert.isPending}
              className="bg-blue-600 text-xs font-bold hover:bg-blue-700"
            >
              {upsert.isPending ? "저장 중..." : "저장"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
